/**
 * How each Connect channel words its brand and competitor questions, and what
 * its recommended competitors compete for. Pure, no React; read by
 * `questions.ts` and by the recommendation call (`server/suggest-context.ts`).
 *
 * Join my survey's words live with it in `join-survey.ts`.
 */

import type { ConnectChannelKey } from './types';

export interface ChannelWords {
    /** What the brand question asks for: "Which {brandNoun} should DANI look
     * at?" — 'brand', 'app' or 'website'. */
    brandNoun: string;
    /** What the brand must be for recommendations to make sense, so the model
     * returns nothing for an agency rather than guessing: "a consumer brand
     * with physical locations". */
    brandKind: string;
    /** What competitors compete with the brand for. Only channels whose use
     * cases ask about competitors have one. */
    competitorArena?: string;
    /** How "Let DANI choose" reads on the competitor question. */
    competitorFallback?: string;
}

const AI_ASSISTANT: ChannelWords = {
    brandNoun: 'brand',
    brandKind: 'a brand consumers ask AI assistants about',
    competitorArena: 'mentions and recommendations in AI assistant answers about the same category',
    competitorFallback: 'DANI picks the brands that come up most alongside yours',
};

export const CHANNEL_WORDS: Record<ConnectChannelKey, ChannelWords> = {
    demographics: { brandNoun: 'brand', brandKind: 'a consumer brand' },
    location: {
        brandNoun: 'brand',
        brandKind: 'a consumer brand with physical locations',
        competitorArena: 'in-person visits to physical stores and venues in the US',
        competitorFallback: 'Uses the nearest competitors to each store',
    },
    app: {
        brandNoun: 'app',
        brandKind: 'a consumer brand or company with a mobile app',
        competitorArena: 'time spent in mobile apps by US consumers',
        competitorFallback: 'DANI picks the apps that compete most for the same users',
    },
    web: {
        brandNoun: 'website',
        brandKind: 'a consumer brand or company with a website',
        competitorArena: 'visits to consumer websites in the US',
        competitorFallback: 'DANI picks the sites that compete most for the same visitors',
    },
    purchase: {
        brandNoun: 'brand',
        brandKind: 'a consumer brand whose products people buy',
        competitorArena: 'consumer purchases in the same product categories',
        competitorFallback: 'DANI picks the brands that compete most for the same baskets',
    },
    'digital-ads': {
        brandNoun: 'brand',
        brandKind: 'a brand that advertises to consumers',
        competitorArena: 'consumer attention to social and digital ads',
        competitorFallback: 'DANI picks the advertisers competing for the same audience',
    },
    media: { brandNoun: 'brand', brandKind: 'a consumer brand' },
    audio: { brandNoun: 'brand', brandKind: 'a consumer brand' },
    'gen-ai-chatgpt': AI_ASSISTANT,
    'gen-ai-gemini': AI_ASSISTANT,
    attitudes: { brandNoun: 'brand', brandKind: 'a consumer brand' },
};
